import React, { useCallback, useEffect, useRef, useState } from 'react';
import useStore from '../../store/useStore';
import { connectionsAPI } from '../../services/api';
import { formatDateTime, formatNumber } from './dataSourceFormat';
import ClickupSettings from './ClickupSettings';
import DefinitionEditor from './DefinitionEditor';
import HaloSettings from './HaloSettings';
import JiraSettings from './JiraSettings';
import SageHrSettings from './SageHrSettings';
import SimproSettings from './SimproSettings';
import ShopifySettings from './ShopifySettings';
import QuickBooksSettings from './QuickBooksSettings';
import IntacctSettings from './IntacctSettings';
import '../../styles/Connections.css';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const STATUS = {
  connected: { label: 'Connected', tone: 'ok' },
  error: { label: 'Some data did not sync', tone: 'warn' },
  needs_reconnect: { label: 'Needs reconnecting', tone: 'bad' },
};

const INTERVAL_LABEL = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

const CATEGORY_LABEL = { landing_page: 'Landing page', download: 'Content download', form: 'Other form' };

const CONCEPTS = [
  { key: 'mql', label: 'MQL', hint: 'A marketing qualified lead. Used by MQLs generated and MQL to SQL conversion.' },
  { key: 'sql', label: 'SQL', hint: 'A sales qualified lead. Used by MQL to SQL conversion and SQL to closed won.' },
  { key: 'proposal', label: 'Proposal', hint: 'A proposal sent. Used by Proposals sent.' },
  { key: 'customer', label: 'New customer', hint: 'A company becoming a customer. Used by New customers.' },
];

const CUSTOM_DATASETS = [
  { name: 'hubspot_companies', object: 'companies', label: 'Companies' },
  { name: 'hubspot_contacts', object: 'contacts', label: 'Contacts' },
  { name: 'hubspot_deals', object: 'deals', label: 'Deals' },
];

/** The company's own HubSpot fields, added as columns on a dataset. */
function CustomFields({ options, value, onChange }) {
  const [dataset, setDataset] = useState(CUSTOM_DATASETS[0].name);
  const [property, setProperty] = useState('');
  const current = CUSTOM_DATASETS.find((d) => d.name === dataset);
  const added = (name) => (value[name] || []).map((f) => f.property);
  const available = (options.fields?.[current.object] || []).filter((f) => f.canAdd && !added(dataset).includes(f.name));
  const labelOf = (d, f) => options.fields?.[d.object]?.find((x) => x.name === f.property)?.label || f.label || f.property;

  const add = () => {
    if (!property) return;
    onChange({ ...value, [dataset]: [...(value[dataset] || []), { property }] });
    setProperty('');
  };
  const remove = (name, field) => onChange({ ...value, [name]: value[name].filter((f) => f.property !== field) });
  const rows = CUSTOM_DATASETS.flatMap((d) => (value[d.name] || []).map((f) => ({ d, f })));

  return (
    <section className="connection-section" aria-labelledby="custom-fields-heading">
      <h4 id="custom-fields-heading">Your own fields</h4>
      <p className="source-hint">
        Add HubSpot fields as columns, to use in reports and measures. Fields holding a contact&apos;s personal details cannot be added.
      </p>
      {rows.length > 0 && (
        <table className="datasets-table connection-forms">
          <thead><tr><th>Field</th><th>Dataset</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {rows.map(({ d, f }) => (
              <tr key={`${d.name}-${f.property}`}>
                <td>{labelOf(d, f)}</td>
                <td>{d.label}</td>
                <td><button type="button" className="btn-secondary btn-small" onClick={() => remove(d.name, f.property)} aria-label={`Remove ${labelOf(d, f)}`}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="custom-field-add">
        <label className="connection-field">
          <span>Dataset</span>
          <select value={dataset} onChange={(e) => { setDataset(e.target.value); setProperty(''); }}>
            {CUSTOM_DATASETS.map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
          </select>
        </label>
        <label className="connection-field">
          <span>Field to add</span>
          <select value={property} onChange={(e) => setProperty(e.target.value)}>
            <option value="">Choose a field</option>
            {available.map((f) => <option key={f.name} value={f.name}>{f.label}</option>)}
          </select>
        </label>
        <button type="button" className="btn-secondary" onClick={add} disabled={!property}>Add field</button>
      </div>
    </section>
  );
}

function HubspotSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const o = res.data;
        const s = connection.settings || {};
        const definitions = { ...connection.definitions };
        // Until stages are chosen, stages labelled as a proposal count.
        if (definitions.proposal?.type === 'deal_stage' && !definitions.proposal.stages) {
          definitions.proposal = { ...definitions.proposal, stages: o.suggestedProposalStages };
        }
        setOptions(o);
        setDraft({
          interval: connection.syncIntervalMinutes,
          pipelines: s.pipelines || [],
          definitions,
          formCategories: s.formCategories || {},
          customFields: s.customFields || {},
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, `Could not read the options from ${connector.title}`)); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from {connector.title}…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          pipelines: draft.pipelines,
          definitions: draft.definitions,
          formCategories: draft.formCategories,
          customFields: draft.customFields,
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{INTERVAL_LABEL(m)}</option>)}
        </select>
      </label>

      <fieldset className="connection-field">
        <legend>Pipelines</legend>
        <p className="source-hint">Deals to include. Leave all unticked to include every pipeline.</p>
        <div className="connection-checks">
          {options.pipelines.map((p) => (
            <label key={p.id}>
              <input
                type="checkbox"
                aria-label={`Pipeline ${p.label}`}
                checked={draft.pipelines.includes(p.id)}
                onChange={() => set({ pipelines: draft.pipelines.includes(p.id) ? draft.pipelines.filter((v) => v !== p.id) : [...draft.pipelines, p.id] })}
              />
              {p.label}
            </label>
          ))}
        </div>
      </fieldset>

      <section className="connection-section" aria-labelledby="definitions-heading">
        <h4 id="definitions-heading">Definitions</h4>
        <p className="source-hint">
          Choose what counts for your company. Reports and KPIs use these definitions. Changing one recalculates past figures on the next full sync, so earlier periods may change.
        </p>
        <div className="definition-list">
          {CONCEPTS.map((c) => (
            <DefinitionEditor
              key={c.key}
              connectorId={connector.id}
              companyId={companyId}
              concept={c.key}
              label={c.label}
              hint={c.hint}
              rule={draft.definitions[c.key]}
              options={options}
              onChange={(rule) => set({ definitions: { ...draft.definitions, [c.key]: rule } })}
            />
          ))}
        </div>
      </section>

      <fieldset className="connection-field">
        <legend>Form categories</legend>
        {!options.formsAvailable && <p className="source-hint">This connection cannot read forms. Connect again with the forms scope to include form submissions.</p>}
        {options.formsAvailable && options.forms.length === 0 && <p className="source-hint">{connector.title} has no forms.</p>}
        {options.forms.length > 0 && (
          <table className="datasets-table connection-forms">
            <thead><tr><th>Form</th><th>Category</th></tr></thead>
            <tbody>
              {options.forms.map((f) => (
                <tr key={f.id}>
                  <td>{f.name}</td>
                  <td>
                    <select
                      aria-label={`Category for ${f.name}`}
                      value={draft.formCategories[f.id] || 'form'}
                      onChange={(e) => set({ formCategories: { ...draft.formCategories, [f.id]: e.target.value } })}
                    >
                      {options.formCategories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </fieldset>

      <CustomFields options={options} value={draft.customFields} onChange={(customFields) => set({ customFields })} />

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}

const SETTINGS_FORMS = { hubspot: HubspotSettings, clickup: ClickupSettings, intacct: IntacctSettings, halo: HaloSettings, jira: JiraSettings, sagehr: SageHrSettings, simpro: SimproSettings, shopify: ShopifySettings, quickbooks: QuickBooksSettings, xero: QuickBooksSettings };

const HEALTH_LABEL = { warn: 'Needs attention', info: 'Worth checking', ok: 'Looks good' };

/** How well the company's data suits the pack's metrics, and what to do about it. */
function DataHealth({ connectorId, companyId, refreshKey }) {
  const [checks, setChecks] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.health(connectorId, companyId)
      .then((res) => { if (!cancelled) setChecks(res.data?.checks || []); })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not check the data')); });
    return () => { cancelled = true; };
  }, [connectorId, companyId, refreshKey]);

  if (error) return <div className="error-message" role="alert">{error}</div>;
  if (!checks) return null;
  if (checks.length === 0) return <p className="source-hint">The data check runs after the first sync.</p>;

  const problems = checks.filter((c) => c.status !== 'ok');
  const fine = checks.filter((c) => c.status === 'ok');
  return (
    <section className="connection-section" aria-labelledby="data-health-heading">
      <h4 id="data-health-heading">Data check</h4>
      {problems.length === 0 && <p className="source-hint">No problems found with this data.</p>}
      <ul className="health-list">
        {problems.map((c) => (
          <li key={c.key} className={`health-item health-${c.status}`}>
            <span className={`connection-status connection-status-${c.status === 'warn' ? 'warn' : 'info'}`}>{HEALTH_LABEL[c.status]}</span>
            <strong>{c.title}</strong>
            <span className="health-detail">{c.detail}</span>
            {c.action && <span className="health-action">{c.action}</span>}
          </li>
        ))}
      </ul>
      {fine.length > 0 && (
        <details className="health-fine">
          <summary>{fine.length} {fine.length === 1 ? 'check' : 'checks'} passed</summary>
          <ul className="health-list">
            {fine.map((c) => <li key={c.key} className="health-item"><strong>{c.title}</strong><span className="health-detail">{c.detail}</span></li>)}
          </ul>
        </details>
      )}
    </section>
  );
}

/** Stored credentials can only change when the signed-in admin confirms their own password. */
function ConfirmPassword({ value, onChange }) {
  return (
    <>
      <label className="connection-field">
        <span>Your Wallboard password</span>
        <input type="password" autoComplete="current-password" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
      <p className="source-hint">Required to change stored credentials.</p>
    </>
  );
}

const AUDIT_LABEL = {
  connected: 'Connected',
  replaced: 'Credentials replaced',
  disconnected: 'Disconnected',
  password_refused: 'Change refused: incorrect password',
};

/** Who has changed the stored credentials, and when. */
function ConnectionHistory({ connectorId, companyId, refreshKey }) {
  const [events, setEvents] = useState(null);

  const open = async (e) => {
    if (!e.currentTarget.open) return;
    try {
      const res = await connectionsAPI.audit(connectorId, companyId);
      setEvents(res.data || []);
    } catch {
      setEvents([]);
    }
  };

  return (
    <details className="connection-settings-wrap" key={refreshKey} onToggle={open}>
      <summary>Credential history</summary>
      {events === null && <p className="source-hint">Loading…</p>}
      {events?.length === 0 && <p className="source-hint">No changes recorded yet.</p>}
      {events?.length > 0 && (
        <table className="datasets-table connection-forms">
          <thead><tr><th>When</th><th>What</th><th>Who</th><th>From</th></tr></thead>
          <tbody>
            {events.map((event, n) => (
              <tr key={`${event.at}-${n}`}>
                <td>{formatDateTime(event.at)}</td>
                <td>{AUDIT_LABEL[event.action] || event.action}</td>
                <td>{event.username || '—'}</td>
                <td>{event.ipAddress || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  );
}

function ConnectForm({ connector, companyId, onConnected }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [token, setToken] = useState('');
  const [fields, setFields] = useState({});
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [signInValues, setSignInValues] = useState({});
  const signInFields = connector.signInFields || [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const signIn = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      setError('');
      // A source whose app is the product's own needs only the sign-in.
      const app = connector.signInServerApp ? {} : { ...signInValues, clientId, clientSecret };
      const res = await connectionsAPI.startOAuth(connector.id, companyId, { ...app, currentPassword });
      window.location.assign(res.data.url);
    } catch (err) {
      setError(describeError(err, 'Could not start the sign-in'));
      setBusy(false);
    }
  };

  const connectWithToken = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      setError('');
      const res = await connectionsAPI.connectWithToken(connector.id, companyId, token, currentPassword);
      setToken('');
      setCurrentPassword('');
      onConnected(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not connect'));
    } finally {
      setBusy(false);
    }
  };

  const connectWithFields = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      setError('');
      const res = await connectionsAPI.connectWithCredentials(connector.id, companyId, { ...fields, currentPassword });
      setFields({});
      setCurrentPassword('');
      onConnected(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not connect'));
    } finally {
      setBusy(false);
    }
  };

  if (!connector.encryptionReady) {
    return <p className="source-hint">Connections are not available yet: CONNECTOR_SECRET_KEY is not set on this server.</p>;
  }

  return (
    <>
      {error && <div className="error-message" role="alert">{error}</div>}
      <p className="source-hint connection-intro">
        Use your company&apos;s own {connector.title} credentials. They are encrypted, used only for your company, and cannot be viewed again.
        {connector.privateAppScopes.length > 0 && ' Both options need these read-only scopes:'}
      </p>
      {connector.privateAppScopes.length > 0 && <code className="connection-scopes">{connector.privateAppScopes.join(' ')}</code>}
      {connector.extraData?.length > 0 && (
        <details className="connection-extra-data">
          <summary>Optional scopes for more data</summary>
          <p className="source-hint">Data without its scope is left out, and everything else still syncs.</p>
          <table className="datasets-table connection-forms">
            <thead><tr><th>Data</th><th>Scope</th></tr></thead>
            <tbody>{connector.extraData.map((d) => <tr key={d.label}><td>{d.label}</td><td><code>{d.scope}</code></td></tr>)}</tbody>
          </table>
        </details>
      )}
      {connector.credentialFields && (
        <form className="connection-token" onSubmit={connectWithFields}>
          <h4>{connector.tokenName}</h4>
          <p className="source-hint">{connector.tokenHelp}</p>
          {connector.credentialFields.map((f) => (
            <label className="connection-field" key={f.key}>
              <span>{f.label}</span>
              <input type={f.secret ? 'password' : 'text'} autoComplete="off" value={fields[f.key] || ''} onChange={(e) => setFields((v) => ({ ...v, [f.key]: e.target.value }))} />
            </label>
          ))}
          <ConfirmPassword value={currentPassword} onChange={setCurrentPassword} />
          <button type="submit" className="btn-primary" disabled={busy || !currentPassword || connector.credentialFields.some((f) => !(fields[f.key] || '').trim())}>{busy ? 'Connecting…' : 'Connect'}</button>
        </form>
      )}
      {!connector.credentialFields && !connector.signInOnly && (
      <details className="connection-token" open>
        <summary>{connector.tokenName}</summary>
        <form onSubmit={connectWithToken}>
          <p className="source-hint">{connector.tokenHelp}</p>
          <label className="connection-field">
            <span>{connector.tokenName}</span>
            <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} />
          </label>
          <ConfirmPassword value={currentPassword} onChange={setCurrentPassword} />
          <button type="submit" className="btn-primary" disabled={busy || !currentPassword || !token.trim()}>{busy ? 'Connecting…' : 'Connect'}</button>
        </form>
      </details>
      )}
      {connector.signInAvailable && connector.signInServerApp && (
        <form className="connection-token" onSubmit={signIn}>
          <p className="source-hint">{connector.signInHelp || `Sign in to ${connector.title} and choose the account to connect. Access then renews automatically.`}</p>
          {!connector.serverAppReady && <p className="source-hint">{connector.title} is not set up on this server yet.</p>}
          {connector.serverAppReady && !connector.redirectUrl && <p className="source-hint">Sign-in is not available: PUBLIC_APP_URL is not set on this server.</p>}
          <ConfirmPassword value={currentPassword} onChange={setCurrentPassword} />
          <button type="submit" className="btn-primary" disabled={busy || !currentPassword || !connector.redirectUrl || !connector.serverAppReady}>Connect to {connector.title}</button>
        </form>
      )}
      {connector.signInAvailable && !connector.signInServerApp && (
      <details className="connection-token" open={Boolean(connector.signInOnly)}>
        <summary>{connector.title} app sign-in</summary>
        <form onSubmit={signIn}>
          <p className="source-hint">
            {connector.signInHelp || `Create an app in ${connector.title} with the scopes above and this redirect URL, then enter its client ID and secret. You sign in once; access then renews automatically.`}
          </p>
          {connector.redirectUrl
            ? <code className="connection-scopes">{connector.redirectUrl}</code>
            : <p className="source-hint">Sign-in is not available: PUBLIC_APP_URL is not set on this server.</p>}
          {signInFields.map((f) => (
            <label className="connection-field" key={f.key}>
              <span>{f.label}</span>
              <input type="text" autoComplete="off" placeholder={f.placeholder || ''} value={signInValues[f.key] || ''} onChange={(e) => setSignInValues((v) => ({ ...v, [f.key]: e.target.value }))} />
            </label>
          ))}
          <label className="connection-field">
            <span>Client ID</span>
            <input type="text" autoComplete="off" value={clientId} onChange={(e) => setClientId(e.target.value)} />
          </label>
          <label className="connection-field">
            <span>Client secret</span>
            <input type="password" autoComplete="off" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
          </label>
          <ConfirmPassword value={currentPassword} onChange={setCurrentPassword} />
          <button type="submit" className="btn-secondary" disabled={busy || !currentPassword || !connector.redirectUrl || !clientId.trim() || !clientSecret.trim() || signInFields.some((f) => !(signInValues[f.key] || '').trim())}>Connect with {connector.title}</button>
        </form>
      </details>
      )}
    </>
  );
}

/** Connect a pack's source system, and see and control its syncs. */
export default function ConnectionPanel({ connectorId }) {
  const companyId = useStore((s) => s.currentCompany?.id);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [disconnectPassword, setDisconnectPassword] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await connectionsAPI.get(connectorId, companyId);
      if (mounted.current) {
        setInfo(res.data);
        setError('');
      }
    } catch (err) {
      if (mounted.current) setError(describeError(err, 'Could not read the connection'));
    }
  }, [connectorId, companyId]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  // Keep the status current while a sync is running or about to start.
  const connection = info?.connection;
  const active = Boolean(connection && connection.status !== 'needs_reconnect'
    && (connection.running || new Date(connection.nextSyncAt).getTime() <= Date.now()));
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [active, load]);

  const act = async (fn, fallback) => {
    try {
      setBusy(true);
      setError('');
      const res = await fn();
      if (res?.data?.connector) setInfo(res.data);
      else await load();
    } catch (err) {
      setError(describeError(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (!info) {
    return (
      <section className="source-card" aria-labelledby="connection-heading">
        <h3 id="connection-heading">Connection</h3>
        {error ? <div className="error-message" role="alert">{error}</div> : <p className="source-hint">Loading…</p>}
      </section>
    );
  }

  const { connector } = info;
  const SettingsForm = SETTINGS_FORMS[connector.id];
  const latestSync = connection?.datasets.map((d) => d.lastRun?.finishedAt || '').join('|');
  const status = connection ? STATUS[connection.status] : null;
  const account = connection?.account?.label || connection?.account?.id;

  return (
    <section className="source-card" aria-labelledby="connection-heading">
      <h3 id="connection-heading">Connection</h3>
      {error && <div className="error-message" role="alert">{error}</div>}

      {!connection && (
        <>
          <p className="source-hint connection-intro">Connect {connector.title} and the wallboard keeps these datasets up to date.</p>
          <ConnectForm connector={connector} companyId={companyId} onConnected={setInfo} />
        </>
      )}

      {connection && (
        <>
          <div className="connection-summary">
            <span className={`connection-status connection-status-${status.tone}`}>{connection.running ? 'Syncing…' : status.label}</span>
            {account && <span>{account}</span>}
            <span>{connection.authType === 'oauth' ? `Signed in with ${connector.title}` : connector.tokenName}</span>
            {connection.connectedBy && <span>Connected by {connection.connectedBy}</span>}
          </div>

          {connection.lastError && <div className="error-message connection-error" role="alert">{connection.lastError}</div>}

          {connection.status === 'needs_reconnect' && (
            <div className="connection-reconnect">
              <p className="source-hint">{connector.title} no longer accepts this connection, so syncing has stopped. Existing data is unchanged.</p>
              <ConnectForm connector={connector} companyId={companyId} onConnected={setInfo} />
            </div>
          )}

          <table className="datasets-table connection-datasets">
            <thead><tr><th>Dataset</th><th>Last synced</th><th>Last run</th><th>Rows read</th></tr></thead>
            <tbody>
              {connection.datasets.map((d) => (
                <tr key={d.name}>
                  <td>{d.name}</td>
                  <td>{d.lastSyncedAt ? formatDateTime(d.lastSyncedAt) : 'Never'}</td>
                  <td>
                    {!d.lastRun && 'Waiting'}
                    {d.lastRun?.status === 'running' && 'Running'}
                    {d.lastRun?.status === 'succeeded' && (d.lastRun.mode === 'full' ? 'Full sync' : 'Changes')}
                    {d.lastRun?.status === 'failed' && <span className="connection-failed">Failed</span>}
                    {d.lastRun?.status === 'skipped' && 'Not included'}
                    {d.lastRun?.message && <div className="source-hint">{d.lastRun.message}</div>}
                  </td>
                  <td>{d.lastRun && d.lastRun.status !== 'running' ? formatNumber(d.lastRun.rows) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="source-hint">
            {INTERVAL_LABEL(connection.syncIntervalMinutes)}{connector.fullSyncNote ? `. ${connector.fullSyncNote}` : ', with a full sync daily.'} Next sync {formatDateTime(connection.nextSyncAt)}.
          </p>

          {connection.apiUsage && (
            <p className="source-hint">
              API calls this month: {formatNumber(connection.apiUsage.calls)} of {formatNumber(connection.apiUsage.budget)}. Syncing stops if the budget is used, and resumes next month.
            </p>
          )}

          <div className="connection-actions">
            <button type="button" className="btn-secondary" disabled={busy || connection.running || connection.status === 'needs_reconnect'} onClick={() => act(() => connectionsAPI.sync(connector.id, companyId, false), 'Could not start the sync')}>
              Sync now
            </button>
            <button type="button" className="btn-secondary" disabled={busy || connection.running || connection.status === 'needs_reconnect'} onClick={() => act(() => connectionsAPI.sync(connector.id, companyId, true), 'Could not start the sync')}>
              Full sync
            </button>
            {!confirming && <button type="button" className="btn-secondary" disabled={busy} onClick={() => setConfirming(true)}>Disconnect</button>}
            {confirming && (
              <span className="connection-confirm">
                <span>Stop syncing and delete the stored credentials? The datasets and their data are kept.</span>
                <label className="connection-field">
                  <span>Your Wallboard password</span>
                  <input type="password" autoComplete="current-password" value={disconnectPassword} onChange={(e) => setDisconnectPassword(e.target.value)} />
                </label>
                <button type="button" className="btn-danger-small" disabled={busy || !disconnectPassword} onClick={() => act(() => connectionsAPI.disconnect(connector.id, companyId, disconnectPassword), 'Could not disconnect').then(() => { setDisconnectPassword(''); setConfirming(false); })}>Disconnect</button>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => { setDisconnectPassword(''); setConfirming(false); }}>Cancel</button>
              </span>
            )}
          </div>

          <DataHealth connectorId={connector.id} companyId={companyId} refreshKey={latestSync} />

          {connection.status !== 'needs_reconnect' && (
            <details className="connection-settings-wrap" onToggle={(e) => setSettingsOpen(e.currentTarget.open)}>
              <summary>Settings</summary>
              {settingsOpen && SettingsForm && <SettingsForm connector={connector} connection={connection} companyId={companyId} onSaved={setInfo} />}
            </details>
          )}
        </>
      )}

      <ConnectionHistory connectorId={connector.id} companyId={companyId} refreshKey={connection?.connectedAt || 'none'} />
    </section>
  );
}
